import { useCallback, useEffect, useReducer } from "react";
import { channelApi } from "../api/channelApi";

/**
 * 폴더별 @멘션 후보 캐시.
 *
 * <h3>왜 채널 멤버 목록을 그냥 못 쓰나</h3>
 * 후보는 <b>서버의 알람 수신자 판정과 같아야 한다</b>
 * (ChannelFeedService.mentionRecipientsWhoCanSee). 화면이 자기 목록을 쓰면 둘이 갈려
 * «골랐는데 알람이 안 가는» 상태가 생기고, 서버만 고치면 후보에는 계속 떠서 사용자는 이유를
 * 알 수 없다. 그래서 후보도 서버에서 받는다.
 *
 * <p>[2026-08-13] 폴더 권한이 폐지돼 지금은 폴더와 무관하게 «채널 참가자 전원» 이다. 캐시가
 * 폴더별인 것은 그대로 둔다 — 판정이 서버에 있으므로 규칙이 다시 갈려도 여기는 손댈 게 없다.
 *
 * <h3>왜 캐시가 모듈 레벨인가</h3>
 * 댓글 입력창은 <b>글마다 하나씩</b> 있고 트리 여기저기에 흩어져 있다. 캐시를 훅 인스턴스마다
 * 두면 목록에 글이 20개일 때 같은 폴더를 20번 조회한다. 후보를 쓰는 컴포넌트마다 프롭으로
 * 내려주는 방법도 있지만 FeedTab 의 전달 경로가 이미 깊어(members 가 6단계까지 내려간다)
 * 프롭을 하나 더 얹는 대신 캐시를 밖으로 뺐다. 폴더는 글보다 훨씬 적어 대개 한두 번으로 끝난다.
 *
 * <h3>로딩 중에는 무엇을 주나</h3>
 * {@code fallback}(보통 채널 멤버 목록)을 그대로 준다. 빈 배열을 주면 팝업이 «후보 없음» 으로
 * 잠깐 닫혔다 열려 깜빡이고, 사용자는 그 사이 친 @ 가 먹통이라고 느낀다. 후보가 조금 넓게
 * 떴다가 좁혀지는 편이 낫다 — 넓게 떠도 서버가 최종적으로 막으므로 알람이 새지는 않는다.
 */

/** key = `${channelId}:${folderId ?? ""}` → 후보 배열 */
const cache = new Map();
/** 같은 key 를 동시에 여러 컴포넌트가 요청해도 한 번만 나가게 */
const inflight = new Set();
/** 결과 도착을 구독 중인 컴포넌트들 */
const listeners = new Set();

const keyOf = (channelId, folderId) => `${channelId}:${folderId ?? ""}`;

/**
 * 폴더 참가자가 바뀌면 그 채널 캐시를 버린다.
 * 안 버리면 방금 폴더에 넣은 사람이 새로고침 전까지 멘션 후보에 안 뜬다.
 */
export function invalidateMentionCandidates(channelId) {
    const prefix = `${channelId}:`;
    for (const k of [...cache.keys()]) {
        if (channelId == null || k.startsWith(prefix)) cache.delete(k);
    }
    listeners.forEach(fn => fn());
}

/**
 * @param {number|null} channelId
 * @param {Array} fallback 조회 전/실패 시 쓸 후보 (채널 멤버 목록)
 * @returns {(folderId: number|null|undefined) => Array} folderId 로 후보를 얻는 함수
 */
export function useMentionCandidates(channelId, fallback) {
    const [, rerender] = useReducer(x => x + 1, 0);

    useEffect(() => {
        listeners.add(rerender);
        return () => listeners.delete(rerender);
    }, []);

    return useCallback((folderId) => {
        if (!channelId) return fallback || [];
        const key = keyOf(channelId, folderId);
        const hit = cache.get(key);
        if (hit) return hit;

        if (!inflight.has(key)) {
            inflight.add(key);
            channelApi.getMentionCandidates(channelId, folderId ?? null)
                .then(r => {
                    cache.set(key, Array.isArray(r) ? r : (r?.data ?? []));
                    listeners.forEach(fn => fn());
                })
                .catch(() => {
                    // 실패는 캐시하지 않는다 — fallback 을 계속 쓰다가 다음 기회에 다시 시도한다.
                    // (여기서 빈 배열을 넣으면 그 폴더는 세션 내내 후보가 사라진다)
                })
                .finally(() => inflight.delete(key));
        }
        return fallback || [];
    }, [channelId, fallback]);
}
